<template>
  <div class="page-container">
    <div class="page-header">
      <h2>疑似关联确认（助理）</h2>
      <p>对领导推送的疑似关联进行线下核实后，在此确认或无法确认（无法确认将退回草稿，领导可再次处理）</p>
    </div>

    <div class="filter-section">
      <el-form inline :model="filterForm" class="filter-form">
        <el-form-item label="核心股东">
          <HolderSuggestInput v-model="filterForm.core_name" type="core" value-key="label" placeholder="汉字/拼音/首字母" style="width: 200px;" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="loadList">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="table-section">
      <el-table v-loading="loading" :data="tableData" border stripe style="width: 100%">
        <el-table-column prop="holder_name" label="普通股东" min-width="120" show-overflow-tooltip />
        <el-table-column prop="address" label="地址" min-width="140" show-overflow-tooltip>
          <template #default="{ row }">{{ row.address || '—' }}</template>
        </el-table-column>
        <el-table-column prop="phone" label="电话" min-width="120" show-overflow-tooltip>
          <template #default="{ row }">{{ row.phone || '—' }}</template>
        </el-table-column>
        <el-table-column prop="id_card_no" label="身份证号" min-width="160" show-overflow-tooltip>
          <template #default="{ row }">{{ row.id_card_no || '—' }}</template>
        </el-table-column>
        <el-table-column prop="trade_summary" label="交易证据链" min-width="200" show-overflow-tooltip>
          <template #default="{ row }">{{ row.trade_summary || '—' }}</template>
        </el-table-column>
        <el-table-column prop="core_name" label="推荐核心股东" min-width="140" show-overflow-tooltip>
          <template #default="{ row }">{{ row.core_name || '—' }}</template>
        </el-table-column>
        <el-table-column prop="combined_confidence" label="综合置信度" min-width="100" align="right">
          <template #default="{ row }">{{ row.combined_confidence }}%</template>
        </el-table-column>
        <el-table-column prop="first_appear_time" label="首次出现时间" width="120" />
        <el-table-column prop="last_appear_time" label="最近出现时间" width="120" />
        <el-table-column prop="latest_hold_vol" label="最新持股数" width="110" align="right">
          <template #default="{ row }">{{ row.latest_hold_vol != null ? Number(row.latest_hold_vol).toLocaleString() : '—' }}</template>
        </el-table-column>
        <el-table-column prop="latest_hold_ratio" label="最新持股比例" width="110" align="right">
          <template #default="{ row }">{{ formatHoldRatio(row.latest_hold_ratio) }}</template>
        </el-table-column>
        <el-table-column prop="push_time" label="推送时间" min-width="160" />
        <el-table-column label="操作" min-width="180" fixed="right">
          <template #default="{ row }">
            <el-button size="small" type="success" @click="openConfirm(row)">确认</el-button>
            <el-button size="small" type="warning" @click="openReject(row)">无法确认</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.page_size"
          :page-sizes="[10, 20, 50]"
          :total="pagination.total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="loadList"
          @current-change="loadList"
        />
      </div>
    </div>

    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="400px" @close="dialogForm.remark = ''">
      <el-form label-width="80px">
        <el-form-item label="备注">
          <el-input v-model="dialogForm.remark" type="textarea" :rows="3" placeholder="选填，线下核实说明" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="submitConfirmOrReject">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import axios from 'axios'
import HolderSuggestInput from '@/components/HolderSuggestInput.vue'

const filterForm = reactive({ core_name: '' })
const tableData = ref([])
const loading = ref(false)
const submitLoading = ref(false)
const pagination = reactive({ page: 1, page_size: 20, total: 0 })
const dialogVisible = ref(false)
const dialogTitle = ref('确认')
const dialogForm = reactive({ suspicious_id: '', confirmed: true, remark: '' })

function loadList() {
  loading.value = true
  axios.get('/api/suspicious/pushed_list/', {
    params: { page: pagination.page, page_size: pagination.page_size, core_name: filterForm.core_name || undefined },
  })
    .then(res => {
      if (res.data.status === 'ok') {
        tableData.value = res.data.data || []
        pagination.total = res.data.total || 0
      } else {
        ElMessage.error(res.data.message || '加载失败')
      }
    })
    .catch(err => {
      ElMessage.error(err.response?.data?.message || err.message || '加载失败')
    })
    .finally(() => { loading.value = false })
}

function handleReset() {
  filterForm.core_name = ''
  pagination.page = 1
  loadList()
}

function openConfirm(row) {
  dialogTitle.value = '确认疑似关联'
  dialogForm.suspicious_id = row.suspicious_id
  dialogForm.confirmed = true
  dialogForm.remark = ''
  dialogVisible.value = true
}

function openReject(row) {
  dialogTitle.value = '无法确认疑似关联'
  dialogForm.suspicious_id = row.suspicious_id
  dialogForm.confirmed = false
  dialogForm.remark = ''
  dialogVisible.value = true
}

function submitConfirmOrReject() {
  submitLoading.value = true
  axios.post('/api/suspicious/secretary_confirm/', {
    suspicious_id: dialogForm.suspicious_id,
    confirmed: dialogForm.confirmed,
    remark: dialogForm.remark,
  })
    .then(res => {
      if (res.data.status === 'ok') {
        ElMessage.success(res.data.message || (dialogForm.confirmed ? '确认成功' : '已退回草稿'))
        dialogVisible.value = false
        loadList()
      } else {
        ElMessage.error(res.data.message || '操作失败')
      }
    })
    .catch(err => {
      ElMessage.error(err.response?.data?.message || err.message || '操作失败')
    })
    .finally(() => { submitLoading.value = false })
}

// 持股比例：0~1 为小数，1~100 为已百分比
function formatHoldRatio(r) {
  if (r === null || r === undefined) return '—'
  const n = Number(r)
  if (Number.isNaN(n)) return '—'
  if (n > 0 && n < 1) return (n * 100).toFixed(4) + '%'
  if (n >= 1 && n <= 100) return n.toFixed(4) + '%'
  return (n * 100).toFixed(4) + '%'
}

onMounted(() => loadList())
</script>

<style scoped>
.page-container { padding: 20px; }
.page-header { margin-bottom: 20px; }
.page-header h2 { margin: 0 0 8px 0; color: #303133; }
.page-header p { margin: 0; color: #606266; }
.filter-section { background: #fff; padding: 20px; border-radius: 4px; margin-bottom: 20px; box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1); }
.table-section { background: #fff; padding: 20px; border-radius: 4px; box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1); }
.pagination-container { margin-top: 20px; text-align: right; }
</style>
